import React from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import amplifyService from '../services/amplify.service.ts';

type LobbyProps = {
  user: any;
};

function TypeWriter({ text, speed = 60 }: { text: string; speed?: number }) {
  const [visibleText, setVisibleText] = React.useState('');
  const [isTyping, setIsTyping] = React.useState(true);

  React.useEffect(() => {
    setVisibleText('');
    setIsTyping(true);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setVisibleText(text.slice(0, i));
      if (i >= text.length) {
        window.clearInterval(id);
        setIsTyping(false);
      }
    }, speed);
    return () => window.clearInterval(id);
  }, [text, speed]);

  return <span className={isTyping ? 'typewriter' : undefined}>{visibleText}</span>;
}

function Lobby({ user }: LobbyProps) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = React.useState<string>('');
  const [totalPuzzles, setTotalPuzzles] = React.useState<number>(0);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const loadName = async () => {
      try {
        if (!user) return;
        // Try backend name first
        const dbUser = await amplifyService.getUser(user.username);
        if (dbUser && dbUser.name) {
          setDisplayName(dbUser.name);
          return;
        }
        // Fallback to Cognito attributes
        const raw = user.attributes?.name || user.attributes?.email || user.username;
        if (typeof raw === 'string') {
          setDisplayName(raw.includes('@') ? raw.split('@')[0] : raw);
        }
      } catch (_e) {
        const raw = user?.attributes?.name || user?.attributes?.email || user?.username || 'Player';
        setDisplayName(typeof raw === 'string' && raw.includes('@') ? raw.split('@')[0] : raw);
      }
    };
    loadName();
  }, [user]);

  React.useEffect(() => {
    const loadTotalPuzzles = async () => {
      try {
        const count = await amplifyService.getTotalPuzzleCount();
        setTotalPuzzles(count);
      } catch (error) {
        console.error('Error loading total puzzles:', error);
      }
    };
    loadTotalPuzzles();
  }, []);

  return (
    <div style={{ width: '100%', padding: '0 1rem' }}>
      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1f2937 100%)',
          color: '#fff',
          padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
          borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.08)',
          marginBottom: isMobile ? '1.5rem' : '1.25rem',
          boxShadow: '0 16px 40px rgba(2,6,23,.6)'
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: isMobile ? '1.5rem' : '1.8rem', 
            letterSpacing: '.3px' 
          }}>
            Welcome{displayName ? <TypeWriter text={`, ${displayName}`} speed={55} /> : ''}
          </h1>
          <p style={{ 
            marginTop: '0.5rem', 
            opacity: 0.8,
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}>
            Choose what you want to do below.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? '1.25rem' : '1rem',
          margin: '0 auto',
          maxWidth: '1100px',
          alignItems: 'stretch'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(79,70,229,.2) 0%, rgba(6,182,212,.2) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: isMobile ? '1.5rem' : '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <h3 style={{ 
              color: '#e5e7eb', 
              marginBottom: isMobile ? '1rem' : '0.75rem',
              fontSize: isMobile ? '1.2rem' : '1.25rem'
            }}>Start Solving</h3>
            <p style={{ 
              color: '#94a3b8', 
              marginBottom: '0.5rem',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
              Jump into the chess board and solve puzzles.
            </p>
            <Button
              className='btn-font-size w-full'
              variant="secondary"
              onClick={() => navigate('/board')}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '0.8rem 1rem',
                borderRadius: '10px',
                fontWeight: 700,
                color: '#e5e7eb'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
              }}
            >
              Go to Board
            </Button>
            <p style={{
              color: '#94a3b8',
              opacity: 0.8,
              fontSize: '0.8rem',
              marginTop: '0.5rem',
              marginBottom: 0
            }}>
              {totalPuzzles} puzzles available
            </p>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,.18) 0%, rgba(6,182,212,.18) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: isMobile ? '1.5rem' : '1.25rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <h3 style={{ 
              color: '#e5e7eb', 
              marginBottom: isMobile ? '1rem' : '0.75rem',
              fontSize: isMobile ? '1.2rem' : '1.25rem'
            }}>Your Progress</h3>
            <p style={{ 
              color: '#94a3b8', 
              marginBottom: '1rem',
              fontSize: isMobile ? '0.9rem' : '1rem'
            }}>
              See your score and statistics.
            </p>
            <Button
              className='btn-font-size w-full'
              variant="secondary"
              onClick={() => navigate('/profile')}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '0.8rem 1rem',
                borderRadius: '10px',
                fontWeight: 700,
                color: '#e5e7eb'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
              }}
            >
              View Profile
            </Button>
            {/* invisible spacer to align buttons across cards */}
            <p aria-hidden="true" style={{
              visibility: 'hidden',
              fontSize: '0.8rem',
              marginTop: '0.5rem',
              marginBottom: 0
            }}>spacer</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Lobby;


